import React, { useMemo, useState } from 'react';
import { pitchSubTypeColor, pitchTypeDisplayName, HeadingChart, BatImpactChart, pitchTypeOrSubType, Checkbox, Button, number } from 'yankees-react-utils';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import ReportCard from '../../../../components/ReportCard';

const StyledCheckboxWrapper = styled.div`
    .nyy-checkbox-radio-container {
        display: flex;
    }
    & input[type="checkbox"]:checked + label::before {
        background-color: ${(props) => props.$color} !important;
        border-color: ${(props) => props.$color} !important;
    }
    & input[type="checkbox"] + label::before {
        border-radius: ${(props) => (props.$square ? '0' : '100%')};
    }
`;

const buildPitchTypesSeries = (
    pitches,
    repertoire,
) => repertoire.map((r) => {
    const pitchType = pitchTypeDisplayName(r);
    return {
        id: pitchType,
        name: pitchType,
        color: pitchSubTypeColor({
            type: r.fxtype,
            fxsubtype: r.fxsubtype,
        }),
        data: pitches.filter((p) => p.fxtype === r.fxtype && p.fxsubtype === r.fxsubtype)
            .map((p) => ({
                fxtype: p.fxtype,
                fxsubtype: p.fxsubtype,
                gameid: p.gameid,
                eventseq: p.eventseq,
                pitchseq: p.pitchseq,
                hbat: p.hbat,
                vbat: p.vbat,
                heading: p.heading,
                inz: p.inz,
                marker: {
                    symbol: p.inz ? 'square' : 'circle',
                    radius: 5,
                    lineWidth: 1,
                    lineColor: '#fff',
                    states: { hover: { radiusPlus: 10, lineWidth: 2, lineColor: '#fff' } },
                },
            })),
    };
});

const pitchOrders = [10, 9, 8, 33, 3, 34, 4, 7, 2];
const getPitchOrderValue = (pt) => {
    const index = pitchOrders.indexOf(pt);

    return index > -1 ? index : 100 + pt;
};

export default function BatTracking({ pitches, isLhb, isLhp, repertoire, hbatVbatEllipses }) {
    const { t: translate } = useTranslation();

    const [selectedPitchTypes, setSelectedPitchTypes] = useState(repertoire.filter((r) => r.fxtype > 1)
        .map((r) => ({ fxtype: r.fxtype, fxsubtype: r.fxsubtype, inz: null })));

    const orderedPitchTypes = repertoire.filter((r) => r.fxtype > 1)
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredOrderedPitchTypes = repertoire
        .filter((r) => r.fxtype > 1 && selectedPitchTypes.some((spt) => spt.fxtype === r.fxtype && spt.fxsubtype === r.fxsubtype))
        .sort((a, b) => getPitchOrderValue(b.fxsubtype || b.fxtype) - getPitchOrderValue(a.fxsubtype || a.fxtype));
    const filteredPitches = pitches.filter((p) => selectedPitchTypes
        .some((spt) => spt.fxtype === p.fxtype && spt.fxsubtype === p.fxsubtype && p.inz === spt.inz));
    const series = buildPitchTypesSeries(filteredPitches, filteredOrderedPitchTypes);
    const headingSeries = series.map((s) => ({
        ...s,
        data: s.data.filter((x) => x.heading !== null).map((d) => ({
            ...d,
            x: d.heading,
            y: filteredOrderedPitchTypes.findIndex(((r) => r.fxtype === d.fxtype && r.fxsubtype === d.fxsubtype)),
        })),
    }));
    const batImpactSeries = series.filter((x) => x.hbat !== null && x.vbat !== null).map((s) => ({
        ...s,
        data: s.data.map((d) => ({
            ...d,
            x: d.hbat,
            y: d.vbat,
        })),
    }));

    const onPitchTypeClick = (pitchType) => {
        if (selectedPitchTypes.some((spt) => spt.fxtype === pitchType.fxtype && spt.fxsubtype === pitchType.fxsubtype && spt.inz === pitchType.inz)) {
            setSelectedPitchTypes(
                selectedPitchTypes.filter((spt) => spt.fxtype !== pitchType.fxtype || spt.fxsubtype !== pitchType.fxsubtype || spt.inz !== pitchType.inz),
            );
        } else {
            setSelectedPitchTypes([...selectedPitchTypes, pitchType]);
        }
    };

    const headingTooltipFormatter = (s, p) => `<b>${s.name}${p.inz ? ' - InZ' : ''}</b><br />Heading: ${number(p.heading, 1)}`;
    const batImpactTooltipFormatter = (s, p) => `<b>${s.name}${p.inz ? ' - InZ' : ''}</b><br />HBat: ${number(p.hbat, 1)}<br />VBat: ${number(p.vbat, 1)}`;
    const pitchTypeLeagueAverages = useMemo(() => repertoire
        .filter((r) => r.fxtype > 1 && selectedPitchTypes.some((spt) => spt.fxtype === r.fxtype && spt.fxsubtype === r.fxsubtype))
        .map((spt) => {
            const match = hbatVbatEllipses.find((e) => e.islhb === isLhb && e.islhp === isLhp && spt.fxtype === e.fxtype && spt.fxsubtype === e.fxsubtype)
            || hbatVbatEllipses.find((e) => e.islhb === isLhb && e.islhp === isLhp && spt.fxtype === e.fxtype && e.fxsubtype === null);
            if (match) {
                return {

                    fxtype: spt.fxtype,
                    fxsubtype: spt.fxsubtype,
                    x: match.centerx,
                    y: match.centerz,
                    minorRadius: match.minorrad,
                    majorRadius: match.majorrad,
                    angle: match.angle,
                };
            }
            return null;
        }).filter((x) => !!x), [hbatVbatEllipses, isLhp, isLhb, selectedPitchTypes, repertoire]);

    return (
        <Row>
            <Col md={12}>
                <ReportCard title={translate('pitcher-overview.sections.bat-tracking')}>
                    {pitches.length ? (
                        <div className="py-2">
                            <div className="d-flex justify-content-around">
                                <div className="text-center">
                                    <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-timing')}</div>
                                    <HeadingChart
                                        seriesData={headingSeries}
                                        orderedPitchTypes={filteredOrderedPitchTypes.map((pt) => pt.fxsubtype || pt.fxtype)}
                                        tooltipFormatter={headingTooltipFormatter}
                                        showLeagueAverageEllipses
                                        disableJitter
                                        sizeMultiplier={0.8}
                                        min={-20}
                                        max={50}
                                    />
                                    <div className="d-flex justify-content-between ms-2 ps-5 pe-3">
                                        <div className="d-flex align-items-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                height="29px"
                                                viewBox="0 -960 960 960"
                                            >
                                                <path d="M360-200 80-480l280-280 56 56-183 184h647v80H233l184 184-57 56Z" />
                                            </svg>
                                            <div className="fs-4 fw-bold ms-1">{translate('pitcher-overview.sections.bat-tracking-late')}</div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="fs-4 fw-bold me-1">{translate('pitcher-overview.sections.bat-tracking-early')}</div>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                height="29px"
                                                viewBox="0 -960 960 960"
                                            >
                                                <path d="m600-200-57-56 184-184H80v-80h647L544-704l56-56 280 280-280 280Z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center d-flex flex-column justify-content-between align-items-center">
                                    <div className="fs-4 fw-bold">{translate('pitcher-overview.sections.bat-tracking-bat-locations')}</div>
                                    <BatImpactChart
                                        seriesData={batImpactSeries}
                                        isLhb={isLhb}
                                        tooltipFormatter={batImpactTooltipFormatter}
                                        pitchTypeLeagueAverages={pitchTypeLeagueAverages}
                                        zoom
                                        pitcherView={false}
                                        sizeMultiplier={0.8}
                                    />
                                </div>
                            </div>
                            <div className="d-flex justify-content-center align-items-center pb-2 mt-4 d-print-none">
                                {orderedPitchTypes.toReversed().map((pt) => (
                                    <div className="d-flex align-items-center mx-2" key={`${pt.fxtype}-${pt.fxsubtype}`}>
                                        <StyledCheckboxWrapper $color={pitchSubTypeColor(pt)}>
                                            <Checkbox
                                                id={pitchTypeOrSubType(pt.fxsubtype || pt.fxtype)}
                                                checked={selectedPitchTypes
                                                    .some((spt) => spt.fxtype === pt.fxtype && spt.fxsubtype === pt.fxsubtype && spt.inz === null)}
                                                onClick={() => onPitchTypeClick({ ...pt, inz: null })}
                                            />
                                        </StyledCheckboxWrapper>
                                        <Button variant="link" onClick={() => setSelectedPitchTypes([{ ...pt, inz: null }])} className="ps-0">
                                            {pitchTypeOrSubType(pt.fxsubtype || pt.fxtype, false, null, translate)}{' '}
                                            ({pitches.find((p) => (p.fxsubtype || p.fxtype) === (pt.fxsubtype || pt.fxtype) && p.inz === null)
                                                ?.trackedswings ?? 0})
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <div className="d-flex justify-content-center align-items-center pb-2 mt-2 d-print-none">
                                {orderedPitchTypes.toReversed().map((pt) => (
                                    <div className="d-flex align-items-center mx-2" key={`${pt.fxtype}-${pt.fxsubtype}`}>
                                        <StyledCheckboxWrapper $color={pitchSubTypeColor(pt)} $square>
                                            <Checkbox
                                                id={`${pitchTypeOrSubType(pt.fxsubtype || pt.fxtype)}-inz`}
                                                checked={selectedPitchTypes
                                                    .some((spt) => spt.fxtype === pt.fxtype && spt.fxsubtype === pt.fxsubtype && spt.inz === 1)}
                                                onClick={() => onPitchTypeClick({ ...pt, inz: 1 })}
                                            />
                                        </StyledCheckboxWrapper>
                                        <Button variant="link" onClick={() => setSelectedPitchTypes([{ ...pt, inz: 1 }])} className="ps-0">
                                            {pitchTypeOrSubType(pt.fxsubtype || pt.fxtype, false, null, translate)} - InZ
                                            ({pitches.find((p) => (p.fxsubtype || p.fxtype) === (pt.fxsubtype || pt.fxtype) && p.inz === 1)
                                                ?.trackedswings ?? 0})
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : <div className="d-flex justify-content-center py-5 text-muted">{translate('pitcher-overview.no-data')}</div>}
                </ReportCard>
            </Col>
        </Row>
    );
}
